import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns/format';

import { DIRECT_DEBIT_BANKS } from '../../../constants/direct-debit-banks';
import { Bank } from '../../../types/payment-config.type';
import { Beneficiary } from '../../../types/beneficiary.type';
import { ValidationErrors } from '../../../utils/validate-schema';
import styles from '../../payment-form/payment-form.module.scss';
import Input from '../../input/input';

export type MobilePaymentData = {
    bankId: number;
    isDirectDebitMode: boolean;
    date?: string;
    referenceCode?: string;
    phone?: string;
    nin?: string;
    name?: string;
    documentType?: 'V' | 'E';
};

type Props = {
    banks: Bank[];
    isDirectDebit: boolean;
    onData: (data: MobilePaymentData) => void;
    errors?: ValidationErrors;
    initialValues?: Partial<MobilePaymentData>;
    titular?: Beneficiary;
};

const MobilePaymentForm = ({
    banks,
    isDirectDebit,
    onData,
    errors,
    initialValues,
    titular,
}: Props) => {
    // The bank catalog is shared: every bank supports Pago Móvil, and a
    // subset additionally supports direct debit. Membership in one list
    // doesn't exclude a bank from the other.
    const eligibleBanks = isDirectDebit
        ? banks.filter((b) => DIRECT_DEBIT_BANKS.includes(b.code))
        : banks;

    // initialValues may carry a bankId from the other gateway (e.g. after an
    // OTP restart followed by a method switch) — drop it if it's not valid here.
    // Falling back to 0 would desync state from the <select>: no option has
    // value="0", so the browser's selectedness algorithm paints the first
    // enabled option (the first bank) while state stays at 0 and validation
    // fails until the user manually reselects a bank.
    const initialBankId =
        initialValues?.bankId &&
        eligibleBanks.some((b) => b.id === initialValues.bankId)
            ? initialValues.bankId
            : (eligibleBanks[0]?.id ?? 0);

    const [bankId, setBankId] = useState(initialBankId);
    const [date, setDate] = useState(initialValues?.date ?? '');
    const [referenceCode, setReferenceCode] = useState(
        initialValues?.referenceCode ?? ''
    );
    const [phone, setPhone] = useState(initialValues?.phone ?? '');
    const [nin, setNin] = useState(initialValues?.nin ?? '');
    const [name, setName] = useState(initialValues?.name ?? '');
    const [documentType, setDocumentType] = useState<'V' | 'E'>(
        initialValues?.documentType ?? 'V'
    );

    const useTitularData = () => {
        if (!titular) return;
        // nid comes as "V-12345678" from NidInput; passports ("P-...") have
        // no V/E equivalent BVC accepts here, so skip nin/documentType and
        // let the user type their cédula manually instead of copying a
        // mismatched passport number into a V/E field.
        const [nidType, ...nidRest] = titular.nid.split('-');
        if (nidType === 'V' || nidType === 'E') {
            setDocumentType(nidType);
            setNin(nidRest.join('-'));
        }
        setPhone(titular.phone.replace(/[\s-]/g, ''));
        setName(titular.fullName);
    };

    useEffect(() => {
        onData({
            bankId,
            isDirectDebitMode: isDirectDebit,
            date: isDirectDebit ? undefined : date,
            referenceCode: isDirectDebit
                ? undefined
                : referenceCode || undefined,
            phone,
            nin,
            name,
            documentType,
        });
    }, [
        bankId,
        isDirectDebit,
        date,
        referenceCode,
        phone,
        nin,
        name,
        documentType,
    ]);

    const selectBank = useCallback((ev: ChangeEvent<HTMLSelectElement>) => {
        setBankId(Number(ev.target.value));
    }, []);

    const selectDate = (strDate: string) => {
        const date = format(
            new Date(strDate.replaceAll('-', '/')),
            'dd/MM/yyyy'
        );
        setDate(date);
    };

    const getError = (field: string) => errors?.get(field);

    return (
        <form className={styles.paymentForm}>
            {!isDirectDebit && (
                <div className={styles.info}>
                    <span>Utilizando Pago Móvil:</span>
                    <b>Banco Venezolano de Crédito</b>
                    <b>J-410713747</b>
                    <b>0424-2479718</b>
                </div>
            )}

            <select
                className={styles.selectField}
                value={bankId}
                onChange={selectBank}
                data-testid="bank-select"
            >
                <option value="" disabled>
                    — Seleccione su Banco —
                </option>
                {eligibleBanks.map((bank) => (
                    <option key={bank.code} value={bank.id}>
                        ({bank.code}) {bank.name}
                    </option>
                ))}
            </select>
            {getError('bankId') && (
                <span className={styles.error}>{getError('bankId')}</span>
            )}

            {titular && (
                <button
                    type="button"
                    className={styles.linkButton}
                    onClick={useTitularData}
                    data-testid="use-personal-data-button"
                >
                    Usar mis datos personales
                </button>
            )}

            <div className={styles.documentRow}>
                <select
                    className={styles.selectFieldInline}
                    value={documentType}
                    onChange={(e) =>
                        setDocumentType(e.target.value as 'V' | 'E')
                    }
                    data-testid="document-type-select"
                >
                    <option value="V">V</option>
                    <option value="E">E</option>
                </select>
                <Input
                    placeholder={
                        isDirectDebit
                            ? 'Cédula del pagador'
                            : 'Cédula de quien pagó'
                    }
                    onInput={setNin}
                    value={nin}
                    maxLength={12}
                    error={getError('nin')}
                    data-testid="payer-nin"
                />
            </div>
            {getError('documentType') && (
                <span className={styles.error}>{getError('documentType')}</span>
            )}

            <Input
                placeholder={
                    isDirectDebit
                        ? 'Teléfono del pagador'
                        : 'Teléfono de quien pagó'
                }
                onInput={setPhone}
                value={phone}
                type="tel"
                maxLength={15}
                error={getError('phone')}
                data-testid="payer-phone"
            />

            <Input
                placeholder={
                    isDirectDebit
                        ? 'Nombre del pagador'
                        : 'Nombre de quien pagó'
                }
                onInput={setName}
                value={name}
                error={getError('name')}
                data-testid="payer-name"
            />

            {!isDirectDebit && (
                <>
                    <Input
                        placeholder={'Fecha del pago'}
                        onInput={selectDate}
                        type="date"
                        error={getError('date')}
                        data-testid="date-input"
                    />

                    <Input
                        placeholder={'Número de referencia'}
                        onInput={setReferenceCode}
                        value={referenceCode}
                        error={getError('referenceCode')}
                        data-testid="reference-input"
                    />
                </>
            )}
        </form>
    );
};

export { MobilePaymentForm };
